import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { Container } from "@mui/material";
import Tab from "@mui/material/Tab";
import { ErrorPage } from "@qubit/autoparts";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import {
  mixpanelTrack,
  useMixpanelContextName,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectUserIsAdmin } from "~/redux/selectors/authSelectors";
import { useGetDashboardQuery } from "~/redux/warehouse/analytics.hooks";
import { AnalyticsDashboardDto } from "~/types/api";

import { isAnalyticsDashboardLocationEvent } from "./analytics.utils";

type Props = { viewTitle?: ViewNameTranslation };

export const getDashboardUrl = (dash: AnalyticsDashboardDto | null) => {
  if (dash == null) return "";
  return `${dash.embedUrl}#titled=false`;
};

export function Analytics({ viewTitle }: Props) {
  const { t } = useTranslation();
  const trackedPageName = useMixpanelPageName();
  useNavbar({ viewTitle });

  const isAdmin = useAppSelector(selectUserIsAdmin);
  const [currentView, setCurrentView] = useState("picker");
  const trackedCurrentView = useMixpanelContextName(
    currentView === "picker" ? "Picker Dashboard" : "Shift Manager Dashboard"
  );

  const {
    data: dashboard,
    isFetching,
    error
  } = useGetDashboardQuery(
    { dashboardType: currentView },
    { refetchOnMountOrArgChange: true }
  );

  const handleMetabaseMessage = useCallback(
    (event: MessageEvent) => {
      try {
        if (!isAnalyticsDashboardLocationEvent(event.data)) return;

        const { location } = event.data.metabase;

        // Only track if there are actual filter parameters in the URL
        if (!location.search) return;

        const searchParams = new URLSearchParams(location.search);
        const appliedFilters: Record<string, string> = {};

        // Build filters object with non-empty values
        searchParams.forEach((value, key) => {
          if (value !== "") {
            appliedFilters[key] = value;
          }
        });

        // Only track if there are actual filters applied
        if (Object.keys(appliedFilters).length > 0) {
          mixpanelTrack({
            type: "Analytics Dashboard Interaction",
            label: "Apply Filter",
            trackedPageName: trackedCurrentView,
            eventProperties: {
              filters: appliedFilters
            }
          });
        }
      } catch (error) {
        console.error("Error handling analytics message event:", error);
      }
    },
    [trackedCurrentView]
  );

  useEffect(() => {
    window.addEventListener("message", handleMetabaseMessage);
    return () => window.removeEventListener("message", handleMetabaseMessage);
  }, [handleMetabaseMessage]);

  const onTabChange = useCallback(
    (_event: React.SyntheticEvent<unknown>, newVal: string) => {
      mixpanelTrack({
        type: "Button Click",
        label:
          newVal === "picker"
            ? "View Picker Dashboard"
            : "View Shift Manager Dashboard",
        trackedPageName
      });
      setCurrentView(newVal);
    },
    [trackedPageName]
  );

  // This function returns an iframe.
  // We cannot inspect dimensions of it due to cross domain protections
  // so make it fill the height and width of the parent.
  // This requires that the parent container have a reasonable height set.
  const getContent = () =>
    !dashboard || isFetching ? (
      <div>Loading</div>
    ) : (
      <iframe
        title="Analytics Dashboard"
        src={getDashboardUrl(dashboard)}
        frameBorder={0}
        allowTransparency
        height="100%"
        width="100%"
      />
    );

  const getFrame = () =>
    isAdmin ? (
      <TabContext value={currentView}>
        <TabList onChange={onTabChange}>
          <Tab label={t("nav.link.picking")} value="picker" />
          <Tab label={t("shift management")} value="shift-manager" />
        </TabList>
        <TabPanel
          value="picker"
          style={{
            height: "660px"
          }}
        >
          {getContent()}
        </TabPanel>
        <TabPanel
          value="shift-manager"
          style={{
            height: "960px"
          }}
        >
          {getContent()}
        </TabPanel>
      </TabContext>
    ) : (
      <TabContext value="picker">
        <TabList onChange={onTabChange}>
          <Tab label="Picking" value="picker" />
        </TabList>
        <TabPanel
          value="picker"
          style={{
            height: "660px"
          }}
        >
          {getContent()}
        </TabPanel>
      </TabContext>
    );

  return (
    <Container>
      {error ? (
        <ErrorPage errorMessage={getMessageFromRtkError(error)} />
      ) : (
        getFrame()
      )}
    </Container>
  );
}
